/**
 * "Show me cases like this": the words and ideas investigators look for when they're putting a string of crimes
 * together (a white SUV, pry marks, a ski mask, catalytic converters…). Used by the similar-cases search to find
 * reports that share them, and to say why each one matched.
 *
 * Each concept is a group of words that mean the same thing in a narrative (an SUV is also a Tahoe, a 4Runner, a
 * Grand Cherokee). Words that are common English (escape, edge, pilot, ranger) are left out on purpose.
 * Colors only count next to a vehicle word ("white Tahoe", "silver in color SUV") — never alone, because
 * "white male" and "black male" are descriptions of people, not cars.
 */

export type ConceptKind = 'vehicle' | 'mo' | 'target' | 'item' | 'weapon' | 'clothing' | 'evidence';
export interface Concept {
  id: string;
  label: string;
  kind: ConceptKind;
  weight: number;
  /** Words or quoted phrases, as typed in a narrative. Single words are stemmed by the search. */
  terms: string[];
}

export const VEHICLE_TYPES: Concept[] = [
  {
    id: 'suv',
    label: 'SUV',
    kind: 'vehicle',
    weight: 8,
    terms: ['suv', 'suvs', '"sport utility"', 'tahoe', 'suburban', 'yukon', 'escalade', 'explorer', 'expedition', 'durango', '4runner', 'highlander', 'rav4', 'crv', '"cr-v"', 'equinox', 'traverse', 'blazer', 'trailblazer', 'pathfinder', 'murano', 'armada', '"grand cherokee"', 'cherokee', 'wrangler', 'jeep', '"santa fe"', 'tucson', 'sorento', 'sportage', 'telluride', 'palisade', 'acadia', 'enclave', 'encore', 'navigator', 'aviator', 'outback', 'forester', 'tiguan', 'atlas', 'sequoia', '"range rover"', 'mountaineer', 'trax', 'kona', '"cx-5"', '"cx-9"', 'cx5', 'cx9', 'outlander', 'envoy'],
  },
  {
    id: 'pickup',
    label: 'pickup truck',
    kind: 'vehicle',
    weight: 8,
    terms: ['pickup', '"pick-up"', '"pick up truck"', 'truck', 'trucks', 'silverado', 'tundra', 'tacoma', 'ridgeline', 'gladiator', '"f-150"', 'f150', '"f-250"', 'f250', '"ram 1500"', '"dodge ram"', 'dakota'],
  },
  {
    id: 'car',
    label: 'car / sedan',
    kind: 'vehicle',
    weight: 5,
    terms: ['sedan', 'coupe', 'camry', 'altima', 'corolla', 'malibu', 'impala', 'charger', '"crown victoria"', 'sonata', 'elantra', 'maxima', 'sentra', 'jetta', 'passat', '"grand marquis"', 'mustang', 'camaro', 'challenger', 'cruze', 'optima', 'avalon', 'taurus', 'lacrosse', '"grand prix"', '"grand am"', 'versa', 'mazda3', 'mazda6', 'prius', '"town car"'],
  },
  {
    id: 'van',
    label: 'van',
    kind: 'vehicle',
    weight: 8,
    terms: ['van', 'vans', 'minivan', 'odyssey', 'sienna', '"grand caravan"', 'caravan', 'pacifica', '"town and country"', '"town & country"', '"cargo van"', 'sprinter', 'promaster', '"ford transit"', '"express van"', '"work van"'],
  },
  {
    id: 'motorcycle',
    label: 'motorcycle / ATV',
    kind: 'vehicle',
    weight: 8,
    terms: ['motorcycle', 'motorbike', '"dirt bike"', 'harley', 'scooter', 'moped', 'atv', '"four wheeler"', '"four-wheeler"', 'utv', '"side by side"'],
  },
];

/** Colors, with the other words people use for them. The first word is the label. */
export const COLORS: Record<string, string[]> = {
  white: ['white'],
  black: ['black'],
  silver: ['silver', 'gray', 'grey'],
  red: ['red', 'maroon', 'burgundy'],
  blue: ['blue', 'navy'],
  green: ['green'],
  tan: ['tan', 'beige', 'gold', 'champagne', 'brown'],
  yellow: ['yellow'],
  orange: ['orange'],
};

export const CONCEPTS: Concept[] = [
  ...VEHICLE_TYPES,
  // How they get in, what they do
  { id: 'forced', label: 'forced entry / pry marks', kind: 'mo', weight: 7, terms: ['pry', 'pried', '"pry marks"', 'crowbar', '"pry bar"', 'prybar', 'screwdriver', '"kicked in"', '"kicked the door"', '"forced entry"', '"forced open"', 'jimmied'] },
  { id: 'window', label: 'broken window', kind: 'mo', weight: 6, terms: ['"broken window"', '"broke the window"', '"window was broken"', '"window was smashed"', '"smashed window"', 'shattered', '"busted window"', '"rock through"', '"brick through"'] },
  { id: 'unlocked', label: 'unlocked cars / car break-ins', kind: 'mo', weight: 7, terms: ['unlocked', 'rummaged', 'ransacked', '"car break"', '"vehicle break"', '"breaking into cars"', '"breaking into vehicles"', '"going through cars"', '"checking door handles"', '"pulling on door handles"', '"glove box"', '"center console"'] },
  { id: 'cutting', label: 'cut lock / fence', kind: 'mo', weight: 6, terms: ['"bolt cutters"', '"cut the lock"', '"lock was cut"', '"cut lock"', '"cut the fence"', '"fence was cut"', 'grinder'] },
  { id: 'drive_off', label: 'gas drive-off', kind: 'mo', weight: 7, terms: ['"drive off"', '"drive-off"', '"pumped gas"', '"without paying"', '"failed to pay"'] },
  { id: 'scam', label: 'scam / fraud call', kind: 'mo', weight: 7, terms: ['scam', 'scammer', '"gift card"', '"gift cards"', 'bitcoin', 'crypto', '"wire transfer"', 'zelle', 'cashapp', '"cash app"', '"grandson"', '"irs"', '"social security"', '"publishers clearing"', '"jury duty"'] },
  { id: 'shoplift', label: 'shoplifting', kind: 'mo', weight: 6, terms: ['shoplift', 'shoplifting', 'concealed', '"passed all points of sale"', '"points of sale"', '"loss prevention"', '"without paying"'] },
  // Where
  { id: 'church', label: 'church', kind: 'target', weight: 6, terms: ['church', 'churches', 'chapel', 'sanctuary'] },
  { id: 'construction', label: 'construction site', kind: 'target', weight: 6, terms: ['"construction site"', '"job site"', '"jobsite"', '"new construction"', 'contractor', 'builder', '"under construction"'] },
  { id: 'store', label: 'store / gas station', kind: 'target', weight: 4, terms: ['"convenience store"', '"gas station"', '"dollar general"', '"family dollar"', 'walmart', '"circle k"', 'quiktrip', 'speedway', '"store clerk"'] },
  { id: 'storage', label: 'storage units / sheds', kind: 'target', weight: 6, terms: ['"storage unit"', '"storage units"', 'shed', 'outbuilding', '"storage building"', 'barn'] },
  { id: 'porch', label: 'packages / porch', kind: 'target', weight: 6, terms: ['package', 'packages', '"porch pirate"', 'amazon', 'fedex', '"front porch"', 'mailbox'] },
  // What they take
  { id: 'catalytic', label: 'catalytic converters', kind: 'item', weight: 10, terms: ['catalytic', '"catalytic converter"', '"catalytic converters"', 'sawzall', '"reciprocating saw"'] },
  { id: 'guns', label: 'guns taken', kind: 'item', weight: 6, terms: ['firearm', 'firearms', 'handgun', 'pistol', 'rifle', 'shotgun', 'revolver', 'glock', '"smith & wesson"', 'taurus', 'ruger', 'ar-15', 'ar15'] },
  { id: 'tools', label: 'tools / equipment', kind: 'item', weight: 5, terms: ['generator', '"power tools"', 'chainsaw', 'dewalt', 'milwaukee', 'makita', '"weed eater"', '"leaf blower"', '"lawn mower"', 'mower', '"pressure washer"', 'welder', 'toolbox'] },
  { id: 'trailer', label: 'trailers', kind: 'item', weight: 7, terms: ['trailer', 'trailers', '"utility trailer"', '"enclosed trailer"', '"car hauler"'] },
  { id: 'copper', label: 'copper / AC units', kind: 'item', weight: 8, terms: ['copper', '"copper wire"', '"ac unit"', '"a/c unit"', '"air conditioning unit"', '"heat pump"', 'condenser', 'scrap'] },
  { id: 'wallet', label: 'purse / wallet / cards', kind: 'item', weight: 4, terms: ['purse', 'wallet', '"debit card"', '"credit card"', '"bank card"', 'checkbook'] },
  { id: 'electronics', label: 'electronics', kind: 'item', weight: 3, terms: ['laptop', 'ipad', 'tablet', 'playstation', 'xbox', 'television', 'tv', '"game system"'] },
  // Weapons used
  { id: 'knife', label: 'knife', kind: 'weapon', weight: 5, terms: ['knife', 'knives', 'machete', '"box cutter"', 'blade'] },
  { id: 'gun_used', label: 'gun shown or fired', kind: 'weapon', weight: 6, terms: ['"pointed a gun"', '"pointed a handgun"', '"pointed a pistol"', '"brandished"', 'gunpoint', '"shots fired"', '"fired shots"', '"shell casings"', 'casings'] },
  // What they wore
  { id: 'hoodie', label: 'hoodie', kind: 'clothing', weight: 4, terms: ['hoodie', 'hooded', '"hood up"', 'sweatshirt'] },
  { id: 'mask', label: 'mask / face covered', kind: 'clothing', weight: 6, terms: ['mask', 'masked', '"ski mask"', 'balaclava', '"face covered"', '"covered his face"', 'bandana', 'shiesty'] },
  { id: 'gloves', label: 'gloves', kind: 'clothing', weight: 4, terms: ['gloves', 'gloved'] },
  { id: 'backpack', label: 'backpack / bag', kind: 'clothing', weight: 3, terms: ['backpack', '"book bag"', 'duffel', '"duffle bag"'] },
  // Leads
  { id: 'video', label: 'camera footage', kind: 'evidence', weight: 2, terms: ['"ring camera"', '"doorbell camera"', '"trail camera"', '"game camera"', '"surveillance video"', 'footage', '"security camera"', '"flock"'] },
];

/** Words in every report that say nothing about the crime. Left out of the "distinctive words" match. */
export const BOILERPLATE = new Set(
  `about above after again against along also another around asked been before being below between both came could day days deputy deputies did does done down during each either else even ever every from further have having here himself herself into itself just last later made make many more most much must myself near never next once only other ours over own reported reporting report reports responded responding response said same says should since some still such than that their theirs them themselves then there these they this those through time times told under until upon very wanted was were what when where which while whom whose will with within without would your yours stated states advised advise contact contacted located location scene arrived arrival upon dispatched dispatch call called calls complainant victim victims suspect suspects subject subjects officer officers sheriff office county residence home house address road street lane drive avenue highway approximately approx observed noticed spoke speak spoke went going came come back front side area unknown also further information info incident case number please note female male black white subjects person persons people stated vehicle vehicles car cars truck property owner owners items item took taken stolen damaged value valued someone something nothing anything around while because however there their left right next morning night evening afternoon today yesterday sometime between later earlier overnight entered enter entering`.split(/\s+/),
);

export type TimeOfDay = 'night' | 'morning' | 'afternoon' | 'evening';
export const TIME_OF_DAY: Record<TimeOfDay, string> = { night: 'overnight (10 PM–5 AM)', morning: 'morning (5–11 AM)', afternoon: 'afternoon (11 AM–5 PM)', evening: 'evening (5–10 PM)' };
export const timeOfDay = (hour: number): TimeOfDay => (hour >= 22 || hour < 5 ? 'night' : hour < 11 ? 'morning' : hour < 17 ? 'afternoon' : 'evening');

const lower = (s: string) => s.toLowerCase();
const unq = (t: string) => t.replace(/^"|"$/g, '');
const words = (s: string) => lower(s).replace(/[^a-z0-9'\- ]+/g, ' ');

/** Which concepts a piece of text mentions (a quick, local check; the database does the real match with stemming). */
export function conceptsIn(text: string): Concept[] {
  const t = ` ${words(text).replace(/\s+/g, ' ')} `;
  return CONCEPTS.filter((c) => c.terms.some((x) => t.includes(` ${unq(x)} `) || t.includes(` ${unq(x)}s `)));
}

/** Colors mentioned right next to a vehicle word ("white Tahoe", "white in color SUV", "silver 4-door sedan"). */
export function vehicleColorsIn(text: string): { color: string; type: string }[] {
  const toks = words(text).split(/\s+/).filter(Boolean);
  const out = new Map<string, { color: string; type: string }>();
  const colorOf = (w: string) => Object.entries(COLORS).find(([, l]) => l.includes(w))?.[0];
  for (let i = 0; i < toks.length; i++) {
    const col = colorOf(toks[i]!);
    if (!col) continue;
    for (let j = i + 1; j <= i + 4 && j < toks.length; j++) {
      const pair = `${toks[j]} ${toks[j + 1] ?? ''}`.trim();
      const vt = VEHICLE_TYPES.find((v) => v.terms.some((x) => unq(x) === toks[j] || unq(x) === `${toks[j]}s` || `${unq(x)}s` === toks[j] || unq(x) === pair));
      if (vt) {
        out.set(`${col}:${vt.id}`, { color: col, type: vt.id });
        break;
      }
    }
  }
  return [...out.values()];
}

/** Candidate distinctive words: not boilerplate, not a number, at least 4 letters. Ranked later by how rare they are. */
export function candidateWords(text: string): string[] {
  const seen = new Set<string>();
  for (const w of words(text).split(/\s+/)) {
    const x = w.replace(/^['-]+|['-]+$/g, '');
    if (x.length < 4 || /^\d+$/.test(x) || BOILERPLATE.has(x) || /'/.test(x)) continue;
    seen.add(x);
  }
  return [...seen].slice(0, 300);
}

/** Time words in a typed description. */
export function timeWordsIn(text: string): TimeOfDay | null {
  const t = lower(text);
  if (/\b(overnight|night|nighttime|late at night|after midnight|early morning hours|2 ?am|3 ?am)\b/.test(t)) return 'night';
  if (/\b(evening|after dark|dusk)\b/.test(t)) return 'evening';
  if (/\b(afternoon|daytime|during the day|broad daylight)\b/.test(t)) return 'afternoon';
  if (/\b(morning)\b/.test(t)) return 'morning';
  return null;
}

/** The tsquery string (websearch syntax) that matches any of a concept's words. */
export const conceptQuery = (c: Concept) => c.terms.join(' OR ');

/** A to_tsquery string for "color next to a vehicle word" (within 1–4 words), single-word vehicle terms only. */
export function colorVehicleQuery(color: string, type: string): string | null {
  const cols = COLORS[color] ?? [];
  const vt = VEHICLE_TYPES.find((v) => v.id === type);
  if (!vt || !cols.length) return null;
  const vs = vt.terms.map(unq).filter((x) => /^[a-z0-9]+$/.test(x));
  const parts: string[] = [];
  for (const c of cols) for (const v of vs) parts.push(`${c} <-> ${v}`, `${c} <2> ${v}`, `${c} <3> ${v}`, `${c} <4> ${v}`);
  return parts.join(' | ');
}
